import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, LayoutTemplate } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { buttonClass } from "./shared";
import { PLAN_TEMPLATES } from "./planTemplates";
import { SitePlan, directionJa } from "./SitePlan";

const PlanSource = ({ projectId, plan, setPlan }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/workflow/${projectId}/plan-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPlan({ kind: "image", template: "", image_id: data.id });
      toast.success("平面図を読み込みました。「撮影指示を保存」で確定します");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "アップロードに失敗しました");
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-xs text-gray-400"><LayoutTemplate className="h-4 w-4" />平面図を選ぶ（テンプレート または 図面画像）</p>
      <div data-testid="plan-template-list" className="flex flex-wrap gap-2">
        {PLAN_TEMPLATES.map((t) => (
          <button type="button" key={t.id} data-testid={`plan-template-${t.id}`} onClick={() => setPlan({ kind: "template", template: t.id, image_id: "" })} aria-pressed={plan.kind === "template" && plan.template === t.id} className={`border px-3 py-2 text-xs transition-colors ${plan.kind === "template" && plan.template === t.id ? "border-accent bg-accent/10 text-accent" : "border-line text-gray-300 hover:border-accent"}`}>{t.name}</button>
        ))}
        <button type="button" data-testid="plan-upload-button" disabled={uploading} onClick={() => fileRef.current?.click()} className={`${buttonClass} !py-2 text-xs ${plan.kind === "image" ? "!border-accent !text-accent" : ""}`}><ImageUp className="h-4 w-4" />{uploading ? "読み込み中…" : "図面画像をアップロード"}</button>
        {plan.kind !== "none" && <button type="button" data-testid="plan-clear" onClick={() => setPlan({ kind: "none", template: "", image_id: "" })} className="px-2 text-xs text-gray-500 hover:text-red-400">平面図を使わない</button>}
        <input ref={fileRef} data-testid="plan-upload-file" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
      </div>
    </div>
  );
};

const color = (photos) => (it) => (photos.some((p) => p.guide_id === it.id && p.status !== "差戻し") ? "#34d399" : "#ff6a1a");

export const PlanEditor = ({ projectId, plan, setPlan, items, setItems, selected, setSelected, photos }) => {
  const place = (pt) => {
    if (selected == null) return toast.info("先に撮影指示の番号を選んでください");
    setItems((list) => list.map((it, i) => (i === selected ? { ...it, ...pt, angle: it.angle ?? -90 } : it)));
  };
  const rotate = (i, angle) => setItems((list) => list.map((it, j) => (j === i ? { ...it, angle } : it)));
  const sel = items[selected];
  const shots = sel ? photos.filter((p) => p.guide_id === sel.id) : [];
  return (
    <div data-testid="plan-editor" className="space-y-3">
      <PlanSource projectId={projectId} plan={plan} setPlan={setPlan} />
      {plan.kind !== "none" && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,560px)_minmax(0,360px)]">
          <div className="space-y-2">
            <div data-testid="plan-number-chips" className="flex flex-wrap gap-1.5">
              {items.map((it, i) => <button type="button" key={it.id} data-testid={`plan-chip-${i}`} onClick={() => setSelected(i)} title={it.title} className={`h-8 min-w-8 rounded-full px-2 font-mono text-xs font-bold ${selected === i ? "bg-accent text-ink" : it.x == null ? "border border-dashed border-gray-600 text-gray-400" : "border border-accent/60 text-accent"}`}>{i + 1}</button>)}
              {items.length === 0 && <p className="text-xs text-gray-500">撮影指示を追加すると番号が表示されます</p>}
            </div>
            <SitePlan plan={plan} items={items} colorFor={color(photos)} selected={selected} editable onSelect={setSelected} onPlace={place} onRotate={rotate} />
            <p className="text-xs text-gray-500">①番号を選ぶ → ②平面図をクリックして撮る位置を置く → ③番号をドラッグして撮る向きを決める。緑＝撮影済み</p>
          </div>
          <div data-testid="plan-selected-info" className="min-w-0 space-y-2 border border-line bg-panel p-3 text-sm">
            {sel ? <>
              <p className="font-bold text-white">{selected + 1}. {sel.title || "（未入力）"}</p>
              <p className="whitespace-pre-wrap text-xs text-gray-400">{sel.instruction}</p>
              <p className="text-xs text-gray-500">{sel.x == null ? "平面図に未配置" : `配置済み・${directionJa(sel.angle)}向き（${Math.round(sel.angle)}°）`}</p>
              {sel.x != null && <button type="button" data-testid="plan-unplace" onClick={() => setItems((l) => l.map((it, i) => (i === selected ? { ...it, x: null, y: null } : it)))} className="text-xs text-gray-500 hover:text-red-400">平面図から外す</button>}
              <div data-testid="plan-selected-photos" className="flex flex-wrap gap-2 pt-1">
                {shots.length === 0 && <p className="text-xs text-gray-600">この位置の写真はまだありません</p>}
                {shots.map((p) => <a key={p.id} data-testid={`plan-photo-${p.id}`} href={`${api.defaults.baseURL}/site-photos/${p.id}/image`} target="_blank" rel="noreferrer"><img src={`${api.defaults.baseURL}/site-photos/${p.id}/image`} alt="" className="h-24 w-24 border border-line object-cover" /></a>)}
              </div>
            </> : <p className="text-xs text-gray-500">番号を押すと、その位置で撮った写真が見られます。</p>}
          </div>
        </div>
      )}
    </div>
  );
};
